/**
 * SpfBudgetClassifyGuard — Sources Spine Phase 3 PR 28.
 *
 * Wraps the existing SourceClassifyControl with a pre-Approve
 * confirmation modal that fires when:
 *
 *   1. the user is about to mark a Source as Approved, AND
 *   2. that Source has a known_provider with an spfInclude that
 *      isn't already in the domain's SPF chain, AND
 *   3. adding the include would push the recursive lookup count
 *      to/past the RFC 7208 §4.6.4 limit of 10.
 *
 * The other classifications (Forwarder / Not-Mine / Dismiss) pass
 * through untouched — they don't add to the SPF surface area, so
 * there's nothing to warn about.
 *
 * The budget data comes from GET /api/spf/:id/budget; we fetch lazily
 * on mount and degrade silently when the resolver is unavailable
 * (better to let the user classify than to block them on a missing
 * service binding). The fetch is one round-trip per (sourceId,
 * domainId) mount — if this ever needs to be cheaper, the parent
 * page can lift the fetch into a context.
 */

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  SourceClassifyControl,
  type SourceClassifyControlProps,
} from "./SourceClassifyControl";
import { Modal } from "@/components/ui/Modal";
import { api, type SpfBudgetResponse, type SpineClassification } from "@/lib/api";

interface Props {
  /** Domain id of the Source — needed to look up the budget. */
  domainId: string;
  /** known_provider_id of the Source, if any. Sources without a known
   *  provider have nothing to project (no spfInclude exists), so the
   *  guard short-circuits to "no warning". */
  knownProviderId: string | null;
  /** Forwarded to the wrapped SourceClassifyControl unchanged. */
  childProps: Omit<Extract<SourceClassifyControlProps, { mode?: "single" }>, "onBeforeClassify">;
}

export function SpfBudgetClassifyGuard({
  domainId,
  knownProviderId,
  childProps,
}: Props) {
  const [budget, setBudget] = useState<SpfBudgetResponse | null>(null);
  // Pending confirmation: when set, the modal is open and we're
  // waiting for the user to confirm or cancel. Resolves the promise
  // returned by onBeforeClassify.
  const [pendingConfirm, setPendingConfirm] = useState<
    null | { target: SpineClassification; resolve: (proceed: boolean) => void }
  >(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getSpfBudget(domainId)
      .then((resp) => {
        if (!cancelled) setBudget(resp);
      })
      .catch(() => {
        // Silent degrade — don't block classification on a budget
        // fetch failure. The guard becomes a no-op pass-through.
      });
    return () => {
      cancelled = true;
    };
  }, [domainId]);

  async function handleBeforeClassify(
    target: SpineClassification
  ): Promise<boolean> {
    // Only Approve has SPF budget implications. Other classifications
    // pass through.
    if (target !== "Approved") return true;
    if (!budget || !knownProviderId) return true;
    // Find this Source's provider in the projections list.
    const projection = budget.projections.find(
      (p) => p.providerId === knownProviderId
    );
    if (!projection) return true;
    if (projection.alreadyIncluded) return true;
    if (
      projection.projectedStatus !== "warning" &&
      projection.projectedStatus !== "exceeded"
    ) {
      return true;
    }
    // Budget threat detected — open the modal and wait for the user.
    return new Promise<boolean>((resolve) => {
      setPendingConfirm({ target, resolve });
    });
  }

  function handleClose(proceed: boolean) {
    pendingConfirm?.resolve(proceed);
    setPendingConfirm(null);
  }

  // Convenience derive — only meaningful while the modal is open.
  const projection =
    pendingConfirm && budget && knownProviderId
      ? budget.projections.find((p) => p.providerId === knownProviderId)
      : null;

  return (
    <>
      <SourceClassifyControl
        {...childProps}
        onBeforeClassify={handleBeforeClassify}
      />
      <Modal
        open={pendingConfirm !== null}
        onOpenChange={(open) => {
          if (!open) handleClose(false);
        }}
      >
        <Modal.Content size="md" title="SPF budget warning">
          {projection && budget && (
            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p>
                  Approving <strong>{projection.providerName}</strong> would
                  add{" "}
                  <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
                    include:{projection.spfInclude}
                  </code>{" "}
                  to your SPF chain, pushing it from{" "}
                  <strong>
                    {budget.currentLookupCount}/{budget.maxLookupCount}
                  </strong>{" "}
                  to at least{" "}
                  <strong>
                    {projection.projectedLookupCount}/{budget.maxLookupCount}
                  </strong>{" "}
                  DNS lookups (RFC 7208 §4.6.4 limit).
                </p>
              </div>
              {projection.projectedStatus === "exceeded" ? (
                <p className="text-rose-700 text-xs">
                  Receivers will treat your SPF as <code>permerror</code> —
                  effectively no SPF authentication at all — until you
                  consolidate the chain.
                </p>
              ) : (
                <p className="text-amber-700 text-xs">
                  You'll be one or two includes away from the limit. Consider
                  consolidating before adding more vendors.
                </p>
              )}
              <p className="text-xs text-slate-500">
                Approving anyway is safe operationally — we'll still record
                the classification — but no SPF DNS change happens here. You
                can resolve the budget separately via the SPF optimization
                wizard.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleClose(true)}
                  className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 font-medium"
                >
                  Approve anyway
                </button>
              </div>
            </div>
          )}
        </Modal.Content>
      </Modal>
    </>
  );
}
